import { useCallback, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Settings, Download, Loader2 } from 'lucide-react';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '-';
  const normalized = Object.is(amount, -0) ? 0 : amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(normalized);
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString();
};

export default function InventoryDamageTable({
  items = [],
  isLoading,
  isLoadingMore,
  hasMoreData,
  onLoadMore,
  totalItems,
  summary,
}) {
  const [columns, setColumns] = useState(() => [
    {
      id: 'createdAt',
      label: 'Date',
      width: 170,
      visible: true,
      align: 'left',
      accessor: (item) => item.createdAt || null,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>{formatDateTime(value)}</div>
      ),
    },
    {
      id: 'itemName',
      label: 'Item Name',
      width: 220,
      visible: true,
      align: 'left',
      accessor: (item) => item.itemName || 'N/A',
      render: (value) => (
        <div className="font-medium" style={{ whiteSpace: 'nowrap' }}>
          {value}
        </div>
      ),
    },
    {
      id: 'sku',
      label: 'SKU',
      width: 140,
      visible: true,
      align: 'left',
      accessor: (item) => item.sku || 'N/A',
      render: (value) => <div style={{ whiteSpace: 'nowrap' }}>{value}</div>,
    },
    {
      id: 'damagedQuantity',
      label: 'Damaged Qty',
      width: 120,
      visible: true,
      align: 'right',
      accessor: (item) => item.damagedQuantity || 0,
      render: (value) => <div style={{ whiteSpace: 'nowrap' }}>{value}</div>,
    },
    {
      id: 'unit',
      label: 'Unit',
      width: 80,
      visible: true,
      align: 'left',
      accessor: (item) => item.unit || '',
      render: (value) => <div style={{ whiteSpace: 'nowrap' }}>{value || '-'}</div>,
    },
    {
      id: 'unitPrice',
      label: 'Unit Price',
      width: 110,
      visible: true,
      align: 'right',
      accessor: (item) => item.unitPrice ?? null,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>{formatCurrency(value)}</div>
      ),
    },
    {
      id: 'costLoss',
      label: 'Cost Loss',
      width: 120,
      visible: true,
      align: 'right',
      accessor: (item) => item.costLoss || 0,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>{formatCurrency(value)}</div>
      ),
    },
    {
      id: 'previousStock',
      label: 'Prev Stock',
      width: 110,
      visible: false,
      align: 'right',
      accessor: (item) => item.previousStock ?? null,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>{value ?? '-'}</div>
      ),
    },
    {
      id: 'newStock',
      label: 'New Stock',
      width: 110,
      visible: false,
      align: 'right',
      accessor: (item) => item.newStock ?? null,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>{value ?? '-'}</div>
      ),
    },
    {
      id: 'source',
      label: 'Source',
      width: 110,
      visible: false,
      align: 'left',
      accessor: (item) => item.source || '',
      render: (value) =>
        value ? (
          <Badge variant="outline">{value}</Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
  ]);

  const visibleColumns = columns.filter((col) => col.visible);

  const toggleColumnVisibility = useCallback((columnId) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.id === columnId ? { ...col, visible: !col.visible } : col
      )
    );
  }, []);

  const handleExportCSV = useCallback(() => {
    if (!items || items.length === 0) return;

    const cols = columns.filter((col) => col.visible);
    const headers = cols.map((col) => col.label).join(',');

    const rows = items.map((item) =>
      cols
        .map((col) => {
          let value = col.accessor(item);

          if (col.id === 'createdAt') {
            value = formatDateTime(value);
          } else if (
            (col.id === 'unitPrice' || col.id === 'costLoss') &&
            typeof value === 'number'
          ) {
            value = value.toFixed(2);
          }

          if (typeof value === 'string') {
            value = value.replace(/"/g, '""');
            if (
              value.includes(',') ||
              value.includes('"') ||
              value.includes('\n')
            ) {
              value = `"${value}"`;
            }
          }

          return value ?? '';
        })
        .join(',')
    );

    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const timestamp = new Date().toISOString().slice(0, 10);
    link.setAttribute('href', url);
    link.setAttribute('download', `inventory-damage-${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [columns, items]);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <CardTitle>Inventory Damage</CardTitle>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Toggle Columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {columns.map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={column.visible}
                    onCheckedChange={() => toggleColumnVisibility(column.id)}
                  >
                    {column.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="outline"
              size="sm"
              className="w-full sm:w-auto"
              onClick={handleExportCSV}
              disabled={!items || items.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {summary && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <div className="rounded-md border bg-muted/30 p-3">
              <div className="text-xs uppercase text-muted-foreground">
                Total Damaged Quantity
              </div>
              <div className="text-lg font-semibold mt-1">
                {summary.totalDamagedQuantity ?? 0}
              </div>
            </div>
            <div className="rounded-md border bg-muted/30 p-3">
              <div className="text-xs uppercase text-muted-foreground">
                Total Cost Loss
              </div>
              <div className="text-lg font-semibold mt-1">
                {formatCurrency(summary.totalCostLoss ?? 0)}
              </div>
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent>
        <div className="border rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {visibleColumns.map((column) => (
                    <TableHead
                      key={column.id}
                      style={{
                        width: column.width,
                        minWidth: column.width,
                        maxWidth: column.width,
                        whiteSpace: 'nowrap',
                        padding: '8px 12px',
                      }}
                      className={
                        column.align === 'right' ? 'text-right' : 'text-left'
                      }
                    >
                      {column.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && items.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumns.length}
                      className="text-center py-8"
                    >
                      <div className="flex items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin mr-2" />
                        <span>Loading...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumns.length}
                      className="text-center py-8"
                    >
                      No damage events found
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, index) => (
                    <TableRow
                      key={item.transactionUuid || index}
                      className={`hover:bg-muted/70 transition-colors ${
                        index % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                      }`}
                    >
                      {visibleColumns.map((column) => {
                        const value = column.accessor(item);
                        const content = column.render
                          ? column.render(value, item)
                          : value;

                        return (
                          <TableCell
                            key={column.id}
                            style={{
                              width: column.width,
                              minWidth: column.width,
                              maxWidth: column.width,
                              padding: '8px 12px',
                            }}
                            className={
                              column.align === 'right'
                                ? 'text-right'
                                : 'text-left'
                            }
                          >
                            {content}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="flex flex-col items-center gap-4 mt-4">
          <div className="text-sm text-muted-foreground">
            Showing {items.length} of {totalItems || items.length} events
          </div>

          {hasMoreData && (
            <Button
              onClick={onLoadMore}
              disabled={isLoadingMore}
              variant="outline"
              className="w-full sm:w-auto"
            >
              {isLoadingMore ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Loading...
                </>
              ) : (
                'Load More'
              )}
            </Button>
          )}
        </div>

        <div className="mt-4 p-3 bg-muted/30 rounded-lg">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>
              {visibleColumns.length} of {columns.length} columns visible
            </span>
            <span>{items.length} damage events loaded</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
